import React from "react";

export const TemplatePreview = ({
  styleType,
  bgImage,
  bgVideo,
  productImages = [],
  onBgImageSelect,
  onProductImageSelect,
}) => {
  const RECOMMENDED_BG = { w: 395, h: 667 };
  const RECOMMENDED_PRODUCT = { w: 300, h: 200 }; // style_03
  const RECOMMENDED_PRODUCT_TALL = { w: 300, h: 350 }; // style_04
  const RECOMMENDED_PRODUCT_EXTRA_TALL = { w: 300, h: 450 }; // style_05
  const RECOMMENDED_SQUARE = { w: 250, h: 250 }; // style_06
  const RECOMMENDED_VERTICAL_SQUARE = { w: 150, h: 280 }; // style_07
  const RECOMMENDED_VERTICAL = { w: 160, h: 350 }; // style_08

  const hiddenFileInputBg = React.useRef(null);
  const hiddenFileInputs = React.useRef([]); 

  const handleBgClick = () => hiddenFileInputBg.current.click();
  const handleProductClick = (index) => hiddenFileInputs.current[index]?.click();

  // Initialize the ref array
  React.useEffect(() => {
    hiddenFileInputs.current = hiddenFileInputs.current.slice(0, 10); // Adjust based on max images needed
  }, []);

  return (
    <div
      style={{
        width: `${RECOMMENDED_BG.w}px`,
        height: `${RECOMMENDED_BG.h}px`,
        position: "relative",
        border: "1px solid #ddd",
        borderRadius: "12px",
        overflow: "hidden",
        background: "#000",
      }}
    >
      {/* Background (clickable) */}
      <img
        src={
          bgImage || `https://placehold.co/${RECOMMENDED_BG.w}x${RECOMMENDED_BG.h}`
        }
        alt="bg placeholder"
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          cursor: "pointer",
        }}
        onClick={handleBgClick}
      />
      <input
        type="file"
        accept="image/*"
        ref={hiddenFileInputBg}
        style={{ display: "none" }}
        onChange={(e) => onBgImageSelect && onBgImageSelect(e.target.files[0])}
      />

      {/* Center product (style_03, 04, 05) */}
      {(styleType === "style_03" ||
        styleType === "style_04" ||
        styleType === "style_05") && (
        <>
          <img
            src={
              productImages[0] ||
              `https://placehold.co/${
                styleType === "style_03"
                  ? `${RECOMMENDED_PRODUCT.w}x${RECOMMENDED_PRODUCT.h}`
                  : styleType === "style_04"
                  ? `${RECOMMENDED_PRODUCT_TALL.w}x${RECOMMENDED_PRODUCT_TALL.h}`
                  : `${RECOMMENDED_PRODUCT_EXTRA_TALL.w}x${RECOMMENDED_PRODUCT_EXTRA_TALL.h}`
              }`
            }
            alt="product placeholder"
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: `${
                styleType === "style_03"
                  ? RECOMMENDED_PRODUCT.w
                  : styleType === "style_04"
                  ? RECOMMENDED_PRODUCT_TALL.w
                  : RECOMMENDED_PRODUCT_EXTRA_TALL.w
              }px`,
              height: `${
                styleType === "style_03"
                  ? RECOMMENDED_PRODUCT.h
                  : styleType === "style_04"
                  ? RECOMMENDED_PRODUCT_TALL.h
                  : RECOMMENDED_PRODUCT_EXTRA_TALL.h
              }px`,
              objectFit: "cover",
              cursor: "pointer",
              borderRadius: "8px",
              background: "#fff",
              boxShadow: "0 6px 18px rgba(0,0,0,0.25)",
            }}
            onClick={() => handleProductClick(0)}
          />
          <input
            type="file"
            accept="image/*"
            ref={(el) => (hiddenFileInputs.current[0] = el)}
            style={{ display: "none" }}
            onChange={(e) =>
              onProductImageSelect && onProductImageSelect(0, e.target.files[0])
            }
          />
        </>
      )}

      {/* Style_06 → two square images vertical */}
      {styleType === "style_06" && (
        <>
          {[0, 1].map((i) => (
            <React.Fragment key={i}>
              <img
                src={
                  productImages[i] ||
                  `https://placehold.co/${RECOMMENDED_SQUARE.w}x${RECOMMENDED_SQUARE.h}`
                }
                alt={`square-${i}`}
                style={{
                  position: "absolute",
                  top: `${80 + i * (RECOMMENDED_SQUARE.h + 10)}px`,
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: `${RECOMMENDED_SQUARE.w}px`,
                  height: `${RECOMMENDED_SQUARE.h}px`,
                  objectFit: "cover",
                  cursor: "pointer",
                  borderRadius: "8px",
                  background: "#fff",
                  boxShadow: "0 6px 18px rgba(0,0,0,0.25)",
                }}
                onClick={() => handleProductClick(i)}
              />
              <input
                type="file"
                accept="image/*"
                ref={(el) => (hiddenFileInputs.current[i] = el)}
                style={{ display: "none" }}
                onChange={(e) =>
                  onProductImageSelect && onProductImageSelect(i, e.target.files[0])
                }
              />
            </React.Fragment>
          ))}
        </>
      )}

      {/* Style_07 → two tall images horizontal */}
      {styleType === "style_07" && (
        <>
          {[0, 1].map((i) => (
            <React.Fragment key={i}>
              <img
                src={
                  productImages[i] ||
                  `https://placehold.co/${RECOMMENDED_VERTICAL_SQUARE.w}x${RECOMMENDED_VERTICAL_SQUARE.h}`
                }
                alt={`vertical ${i}`}
                style={{
                  position: "absolute",
                  top: "40%",
                  left: `${35 + i * (RECOMMENDED_VERTICAL_SQUARE.w + 5)}px`,
                  transform: `translateY(-50%)`,
                  width: `${RECOMMENDED_VERTICAL_SQUARE.w}px`,
                  height: `${RECOMMENDED_VERTICAL_SQUARE.h}px`,
                  objectFit: "cover",
                  cursor: "pointer",
                  borderRadius: "8px",
                  background: "#fff",
                  boxShadow: "0 6px 18px rgba(0,0,0,0.25)",
                }}
                onClick={() => handleProductClick(i)}
              />
              <input
                type="file"
                accept="image/*"
                ref={(el) => (hiddenFileInputs.current[i] = el)}
                style={{ display: "none" }}
                onChange={(e) =>
                  onProductImageSelect && onProductImageSelect(i, e.target.files[0])
                }
              />
            </React.Fragment>
          ))}
        </>
      )}

      {/* Style_08 → two tall images horizontal */}
      {styleType === "style_08" && (
        <>
          {[0, 1].map((i) => (
            <React.Fragment key={i}>
              <img
                src={
                  productImages[i] ||
                  `https://placehold.co/${RECOMMENDED_VERTICAL.w}x${RECOMMENDED_VERTICAL.h}`
                }
                alt={`vertical ${i}`}
                style={{
                  position: "absolute",
                  top: "45%",
                  left: `${25 + i * (RECOMMENDED_VERTICAL.w + 5)}px`,
                  transform: `translateY(-50%)`,
                  width: `${RECOMMENDED_VERTICAL.w}px`,
                  height: `${RECOMMENDED_VERTICAL.h}px`,
                  objectFit: "cover",
                  cursor: "pointer",
                  borderRadius: "8px",
                  background: "#fff",
                  boxShadow: "0 6px 18px rgba(0,0,0,0.25)",
                }}
                onClick={() => handleProductClick(i)}
              />
              <input
                type="file"
                accept="image/*"
                ref={(el) => (hiddenFileInputs.current[i] = el)}
                style={{ display: "none" }}
                onChange={(e) =>
                  onProductImageSelect && onProductImageSelect(i, e.target.files[0])
                }
              />
            </React.Fragment>
          ))}
        </>
      )}

      {/* Style_09 → 2 horizontal + 1 centered below */}
      {styleType === "style_09" && (
        <>
          {[0, 1, 2].map((i) => (
            <React.Fragment key={i}>
              <img
                src={
                  productImages[i] ||
                  `https://placehold.co/${RECOMMENDED_VERTICAL_SQUARE.w}x${RECOMMENDED_VERTICAL_SQUARE.h}`
                }
                alt={`s9-${i}`}
                style={{
                  position: "absolute",
                  top: i < 2 ? "30%" : "73%",
                  left: i < 2 
                    ? `${33 + i * (RECOMMENDED_VERTICAL_SQUARE.w + 10)}px`
                    : "50%",
                  transform: i < 2 
                    ? `translateY(-50%)`
                    : "translateX(-50%) translateY(-50%)",
                  width: `${RECOMMENDED_VERTICAL_SQUARE.w}px`,
                  height: `${RECOMMENDED_VERTICAL_SQUARE.h}px`,
                  objectFit: "cover",
                  cursor: "pointer",
                  borderRadius: "8px",
                  background: "#fff",
                  boxShadow: "0 6px 18px rgba(0,0,0,0.25)",
                }}
                onClick={() => handleProductClick(i)}
              />
              <input
                type="file"
                accept="image/*"
                ref={(el) => (hiddenFileInputs.current[i] = el)}
                style={{ display: "none" }}
                onChange={(e) =>
                  onProductImageSelect && onProductImageSelect(i, e.target.files[0])
                }
              />
            </React.Fragment>
          ))}
        </>
      )}

      {/* Style_10 → 2 horizontal top + 1 wide bottom */}
      {styleType === "style_10" && (
        <>
          {[0, 1, 2].map((i) => (
            <React.Fragment key={i}>
              <img
                src={
                  productImages[i] ||
                  (i < 2 
                    ? `https://placehold.co/${RECOMMENDED_VERTICAL_SQUARE.w}x${RECOMMENDED_VERTICAL_SQUARE.h}`
                    : `https://placehold.co/${RECOMMENDED_VERTICAL_SQUARE.w * 2 + 10}x${RECOMMENDED_VERTICAL_SQUARE.h}`)
                }
                alt={`s10-${i}`}
                style={{
                  position: "absolute",
                  top: i < 2 ? "27%" : "70%",
                  left: i < 2 
                    ? `${40 + i * (RECOMMENDED_VERTICAL_SQUARE.w + 10)}px`
                    : "50%",
                  transform: i < 2 
                    ? `translateY(-50%)`
                    : "translateX(-50%) translateY(-50%)",
                  width: i < 2 
                    ? `${RECOMMENDED_VERTICAL_SQUARE.w}px`
                    : `${RECOMMENDED_VERTICAL_SQUARE.w * 2 + 10}px`,
                  height: `${RECOMMENDED_VERTICAL_SQUARE.h}px`,
                  objectFit: "cover",
                  cursor: "pointer",
                  borderRadius: "8px",
                  background: "#fff",
                  boxShadow: "0 6px 18px rgba(0,0,0,0.25)",
                }}
                onClick={() => handleProductClick(i)}
              />
              <input
                type="file"
                accept="image/*"
                ref={(el) => (hiddenFileInputs.current[i] = el)}
                style={{ display: "none" }}
                onChange={(e) =>
                  onProductImageSelect && onProductImageSelect(i, e.target.files[0])
                }
              />
            </React.Fragment>
          ))}
        </>
      )}

      {/* Style_11 → 2 cols (left: 2 stacked, right: 1 full height) */}
      {styleType === "style_11" && (
        <>
          {[0, 1, 2].map((i) => (
            <React.Fragment key={i}>
              <img
                src={
                  productImages[i] ||
                  (i < 2 
                    ? `https://placehold.co/${RECOMMENDED_VERTICAL.w - 2}x${(RECOMMENDED_VERTICAL.h * 1 + 18) / 2}`
                    : `https://placehold.co/${RECOMMENDED_VERTICAL.w}x${RECOMMENDED_VERTICAL.h * 1 + 25}`)
                }
                alt={i < 2 ? `s11-left-${i}` : "s11-right"}
                style={{
                  position: "absolute",
                  top: i < 2 
                    ? `${150 + i * (RECOMMENDED_VERTICAL.h - 160)}px`
                    : "50.5%",
                  left: i < 2 ? "25px" : "50%",
                  transform: i < 2 ? "none" : "translateY(-50%)",
                  width: i < 2 
                    ? `${RECOMMENDED_VERTICAL.w - 2}px`
                    : `${RECOMMENDED_VERTICAL.w}px`,
                  height: i < 2 
                    ? `${(RECOMMENDED_VERTICAL.h * 1 + 18) / 2}px`
                    : `${RECOMMENDED_VERTICAL.h * 1 + 25}px`,
                  objectFit: "cover",
                  cursor: "pointer",
                  borderRadius: "8px",
                  background: "#fff",
                  boxShadow: "0 6px 18px rgba(0,0,0,0.25)",
                }}
                onClick={() => handleProductClick(i)}
              />
              <input
                type="file"
                accept="image/*"
                ref={(el) => (hiddenFileInputs.current[i] = el)}
                style={{ display: "none" }}
                onChange={(e) =>
                  onProductImageSelect && onProductImageSelect(i, e.target.files[0])
                }
              />
            </React.Fragment>
          ))}
        </>
      )}

      {/* Style_12 → right tall image, left 3 stacked images */}
      {styleType === "style_12" && (
        <>
          {[0, 1, 2, 3].map((i) => (
            <React.Fragment key={i}>
              {i < 3 ? (
                <div
                  style={{
                    position: "absolute",
                    top: `${40 + i * (RECOMMENDED_VERTICAL.h - 150)}px`,
                    left: "20px",
                    width: `${RECOMMENDED_VERTICAL.w + 20}px`,
                    height: `${(RECOMMENDED_VERTICAL.h * 1 + 40) / 2}px`,
                    borderRadius: "8px",
                    overflow: "hidden",
                    boxShadow: "0 6px 18px rgba(0,0,0,0.25)",
                    cursor: "pointer",
                  }}
                  onClick={() => handleProductClick(i)}>
                  <img
                    src={
                      productImages[i] ||
                      `https://placehold.co/${RECOMMENDED_VERTICAL.w}x${(RECOMMENDED_VERTICAL.h * 1 + 40) / 2}`
                    }
                    alt={`s12-left-${i}`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      padding: "8px",
                      background:
                        "linear-gradient(to top, rgba(0,0,0,0.7) 100%, rgba(0,0,0,0) 0%)",
                      color: "#CCC",
                      fontSize: "12px",
                      lineHeight: "14px",
                    }}
                  >
                    <div>Line 1</div>
                    <div>Line 2</div>
                    <div>Line 3</div>
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    right: "20px",
                    transform: "translateY(-50%)",
                    width: `${RECOMMENDED_VERTICAL.w}px`,
                    height: `${RECOMMENDED_VERTICAL.h * 1 + 50}px`,
                    borderRadius: "8px",
                    overflow: "hidden",
                    boxShadow: "0 6px 18px rgba(0,0,0,0.25)",
                    cursor: "pointer",
                  }}
                  onClick={() => handleProductClick(i)}
                >
                  <img
                    src={
                      productImages[i] ||
                      `https://placehold.co/${RECOMMENDED_VERTICAL.w}x${RECOMMENDED_VERTICAL.h * 1 + 50}`
                    }
                    alt="s12-right"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      padding: "8px",
                      background:
                        "linear-gradient(to top, rgba(0,0,0,0.7) 100%, rgba(0,0,0,0) 0%)",
                      color: "#CCC",
                      fontSize: "12px",
                      lineHeight: "14px",
                    }}>
                    <div>Line 1</div>
                    <div>Line 2</div>
                    <div>Line 3</div>
                  </div>
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                ref={(el) => (hiddenFileInputs.current[i] = el)}
                style={{ display: "none" }}
                onChange={(e) =>
                  onProductImageSelect && onProductImageSelect(i, e.target.files[0])
                }
              />
            </React.Fragment>
          ))}
        </>
      )}

      {/* Style_13 → 2 columns (left: 2 stacked, right: 2 stacked) with text below each image */}
      {styleType === "style_13" && (
        <>
          {[0, 1, 2, 3].map((i) => (
            <React.Fragment key={`s13-${i}`}>
              {/* Image container */}
              <img
                src={
                  productImages[i] ||
                  `https://placehold.co/${RECOMMENDED_VERTICAL.w}x${(RECOMMENDED_VERTICAL.h * 1 + 80) / 2}`
                }
                alt={`s13-${i}`}
                style={{
                  position: "absolute",
                  top: `${60 + Math.floor(i / 2) * (((RECOMMENDED_VERTICAL.h * 1 + 20) / 2) + 80 + 10)}px`,
                  left: i % 2 === 0 ? "30px" : `calc(100% - ${RECOMMENDED_VERTICAL.w + 30}px)`,
                  width: `${RECOMMENDED_VERTICAL.w}px`,
                  height: `${(RECOMMENDED_VERTICAL.h * 1 + 80) / 2}px`,
                  objectFit: "cover",
                  cursor: "pointer",
                  borderTopLeftRadius: "8px",
                  borderTopRightRadius: "8px",
                  background: "#fff",
                  boxShadow: "0 6px 18px rgba(0,0,0,0.25)",
                }}
                onClick={() => handleProductClick(i)}
              />

              {/* Text container below each image */}
              <div
                style={{
                  position: "absolute",
                  top: `${90 + Math.floor(i / 2) * (((RECOMMENDED_VERTICAL.h * 1 + 20) / 2) + 80 + 10) + ((RECOMMENDED_VERTICAL.h * 1 + 20) / 2)}px`, // Right below the image
                  left: i % 2 === 0 ? "30px" : `calc(100% - ${RECOMMENDED_VERTICAL.w + 30}px)`,
                  width: `${RECOMMENDED_VERTICAL.w}px`,
                  height: "50px",
                  background: "rgba(255, 255, 255, 0.95)",
                  borderBottomLeftRadius: "8px",
                  borderBottomRightRadius: "8px",
                  padding: "12px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  textAlign: "center",
                }}
              >
                <div style={{
                  fontSize: "12px",
                  color: "#666",
                  lineHeight: "1.2",
                }}>
                  Line 1 - Image {i + 1}
                </div>
                <div style={{
                  fontSize: "12px",
                  color: "#666",
                  lineHeight: "1.2"
                }}>
                  Line 2 - Image {i + 1}
                </div>
                 <div style={{
                  fontSize: "12px",
                  color: "#666",
                  lineHeight: "1.2"
                }}>
                  Line 3 - Image {i + 1}
                </div>
              </div>

              <input
                type="file"
                accept="image/*"
                ref={(el) => (hiddenFileInputs.current[i] = el)}
                style={{ display: "none" }}
                onChange={(e) =>
                  onProductImageSelect && onProductImageSelect(i, e.target.files[0])
                }
              />
            </React.Fragment>
          ))}
        </>
      )}

      {/* Style_14 → 2 columns (Left lower, Right higher) */}
      {styleType === "style_14" && (
        <>
          {[0, 1, 2, 3].map((i) => {
            const isLeft = i % 2 === 0; // Even = Left
            const row = Math.floor(i / 2); // Row index (0 = top, 1 = bottom)
            const imageHeight = (RECOMMENDED_VERTICAL.h * 1 + 100) / 2;
            const textHeight = 50;
            const totalHeight = imageHeight + textHeight + 20; // space between blocks

            // Offsets for staggered effect
            const offsetTop = isLeft ? 60 : 20; // left column lower, right higher

            return (
              <React.Fragment key={`s14-${i}`}>
                {/* Image */}
                <img
                  src={
                    productImages[i] ||
                    `https://placehold.co/${RECOMMENDED_VERTICAL.w}x${imageHeight}`
                  }
                  alt={`s14-${i}`}
                  style={{
                    position: "absolute",
                    top: `${row * totalHeight + offsetTop}px`,
                    left: isLeft
                      ? "30px"
                      : `calc(100% - ${RECOMMENDED_VERTICAL.w + 30}px)`,
                    width: `${RECOMMENDED_VERTICAL.w}px`,
                    height: `${imageHeight}px`,
                    objectFit: "cover",
                    cursor: "pointer",
                    borderTopLeftRadius: "8px",
                    borderTopRightRadius: "8px",
                    background: "#fff",
                    boxShadow: "0 6px 18px rgba(0,0,0,0.25)",
                  }}
                  onClick={() => handleProductClick(i)}
                />

                {/* Text */}
                <div
                  style={{
                    position: "absolute",
                    top: `${row * totalHeight + offsetTop + imageHeight}px`, // below image
                    left: isLeft
                      ? "30px"
                      : `calc(100% - ${RECOMMENDED_VERTICAL.w + 30}px)`,
                    width: `${RECOMMENDED_VERTICAL.w}px`,
                    height: `${textHeight}px`,
                    background: "#b9b9b9ff",
                    borderBottomLeftRadius: "8px",
                    borderBottomRightRadius: "8px",
                    padding: "8px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    textAlign: "center",
                  }}
                >
                  <div style={{ fontSize: "12px", color: "#000", lineHeight: "1.2" }}>
                    Line 1 - Image {i + 1}
                  </div>
                  <div style={{ fontSize: "12px", color: "#000", lineHeight: "1.2" }}>
                    Line 2 - Image {i + 1}
                  </div>
                </div>

                {/* File Input */}
                <input
                  type="file"
                  accept="image/*"
                  ref={(el) => (hiddenFileInputs.current[i] = el)}
                  style={{ display: "none" }}
                  onChange={(e) =>
                    onProductImageSelect && onProductImageSelect(i, e.target.files[0])
                  }
                />
              </React.Fragment>
            );
          })}
        </>
      )}

      {/* Style_12 → right tall image, left 3 stacked images */}
      {styleType === "style_15" && (
        <>
          {[0, 1, 2, 3, 4].map((i) => {
            const isLeft = i < 3; // first 3 images go to left column
            const colWidth = RECOMMENDED_VERTICAL.w;
            const imgHeight = isLeft
              ? (RECOMMENDED_VERTICAL.h * 1 - 80) / 2 // left uses your same height
              : RECOMMENDED_VERTICAL.h - 120; // right uses your same half height

            // positions (unchanged logic)
            const top = isLeft
              ? 40 + i * (imgHeight + 60) // left stacked
              : 40 + (i - 3) * (imgHeight + 60); // right stacked
            const left = isLeft ? "20px" : `calc(100% - ${colWidth + 30}px)`;

            return (
              <React.Fragment key={`s15-${i}`}>
                {/* Image */}
                <div
                  style={{
                    position: "absolute",
                    top: `${top}px`,
                    left,
                    width: `${colWidth}px`,
                    height: `${imgHeight}px`,
                    borderRadius: "8px",
                    overflow: "hidden",
                    boxShadow: "0 6px 18px rgba(0,0,0,0.25)",
                    cursor: "pointer",
                    background: "#fff",
                  }}
                  onClick={() => handleProductClick(i)}
                >
                  <img
                    src={
                      productImages[i] ||
                      `https://placehold.co/${colWidth}x${imgHeight}`
                    }
                    alt={`s15-${i}`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                </div>

                {/* Text container below */}
                <div
                  style={{
                    position: "absolute",
                    top: `${top + imgHeight}px`,
                    left,
                    width: `${colWidth}px`,
                    height: "50px",
                    background: "#b9b9b9ff",
                    borderBottomLeftRadius: "8px",
                    borderBottomRightRadius: "8px",
                    padding: "8px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    textAlign: "center",
                  }}
                >
                  <div style={{ fontSize: "12px", color: "#000", lineHeight: "1.2" }}>
                    Line 1 - Image {i + 1}
                  </div>
                  <div style={{ fontSize: "12px", color: "#000", lineHeight: "1.2" }}>
                    Line 2 - Image {i + 1}
                  </div>
                </div>

                {/* File input */}
                <input
                  type="file"
                  accept="image/*"
                  ref={(el) => (hiddenFileInputs.current[i] = el)}
                  style={{ display: "none" }}
                  onChange={(e) =>
                    onProductImageSelect && onProductImageSelect(i, e.target.files[0])
                  }
                />
              </React.Fragment>
            );
          })}
        </>
      )}

      {/* Style_16 → Left: short + long, Right: long + short, then full-width row below.
      Text containers added under every image (same style as style_15). */}
      {styleType === "style_16" && (
        <>
          {[0, 1, 2, 3, 4].map((i) => {
            const colWidth = RECOMMENDED_VERTICAL.w + 15;
            const smallHeight = RECOMMENDED_VERTICAL.h - 250; // your shorter height
            const largeHeight = RECOMMENDED_VERTICAL.h - 120;       // your longer height
            const gap = 53; // spacing between stacked items
            const startTop = 40; // top margin

            let top = 0;
            let left = "0px";
            let width = colWidth;
            let height = smallHeight;

            if (i === 0) {
              // Left col top (short)
              top = startTop;
              left = "20px";
              height = smallHeight;
            } else if (i === 1) {
              // Left col bottom (long)
              top = startTop + smallHeight + gap;
              left = "20px";
              height = largeHeight;
            } else if (i === 2) {
              // Right col top (long)
              top = startTop;
              left = `calc(100% - ${colWidth + 20}px)`;
              height = largeHeight;
            } else if (i === 3) {
              // Right col bottom (short)
              top = startTop + largeHeight + gap;
              left = `calc(100% - ${colWidth + 20}px)`;
              height = smallHeight;
            } else if (i === 4) {
              // Full-width row under both columns
              top = startTop + largeHeight + smallHeight + gap * 2; // below both stacks
              left = "20px";
              width = colWidth * 2; // span both columns + middle gap
              height = smallHeight;
            }

            const textTop = top + height; // position for text box under image

            return (
              <React.Fragment key={`s16-${i}`}>
                {/* Image box */}
                <div
                  style={{
                    position: "absolute",
                    top: `${top}px`,
                    left,
                    width: `${width}px`,
                    height: `${height}px`,
                    borderRadius: "8px",
                    overflow: "hidden",
                    boxShadow: "0 6px 18px rgba(0,0,0,0.25)",
                    cursor: "pointer",
                    background: "#fff",
                    zIndex: 2,
                  }}
                  onClick={() => handleProductClick(i)}
                >
                  <img
                    src={
                      productImages[i] ||
                      `https://placehold.co/${Math.round(width)}x${Math.round(height)}`
                    }
                    alt={`s16-${i}`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                </div>

                {/* Text container below image (50px height) */}
                <div
                  style={{
                    position: "absolute",
                    top: `${textTop}px`,
                    left,
                    width: `${width}px`,
                    height: "50px",
                    background: "#b9b9b9ff",
                    borderBottomLeftRadius: "8px",
                    borderBottomRightRadius: "8px",
                    padding: "8px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    textAlign: "center",
                    zIndex: 2,
                  }}
                >
                  <div style={{ fontSize: "12px", color: "#000", lineHeight: "1.2" }}>
                    Line 1 - Image {i + 1}
                  </div>
                  <div style={{ fontSize: "12px", color: "#000", lineHeight: "1.2" }}>
                    Line 2 - Image {i + 1}
                  </div>
                </div>

                {/* Hidden file input for this slot */}
                <input
                  type="file"
                  accept="image/*"
                  ref={(el) => (hiddenFileInputs.current[i] = el)}
                  style={{ display: "none" }}
                  onChange={(e) =>
                    onProductImageSelect && onProductImageSelect(i, e.target.files[0])
                  }
                />
              </React.Fragment>
            );
          })}
        </>
      )}

      {styleType === "style_17" && (
        <>
          {[0, 1, 2, 3, 4].map((i) => {
            const parentWidth = 395;
            const gap = 5; // spacing between columns
            const totalGap = gap * 4; // 4 gaps for 5 cols
            const colWidth = (parentWidth - totalGap) / 5; // fit inside 395px
            const imgHeight = RECOMMENDED_VERTICAL.h * 1.4; // tall height

            const left = i * (colWidth + gap); // position each col
            const top = 40; // same top
            const textTop = top + imgHeight;

            return (
              <React.Fragment key={`s17-${i}`}>
                {/* Image */}
                <div
                  style={{
                    position: "absolute",
                    top: `${top}px`,
                    left: `${left}px`,
                    width: `${colWidth}px`,
                    height: `${imgHeight}px`,
                    borderRadius: "8px",
                    overflow: "hidden",
                    boxShadow: "0 6px 18px rgba(0,0,0,0.25)",
                    cursor: "pointer",
                    background: "#fff",
                  }}
                  onClick={() => handleProductClick(i)}
                >
                  <img
                    src={
                      productImages[i] ||
                      `https://placehold.co/${Math.round(colWidth)}x${Math.round(
                        imgHeight
                      )}`
                    }
                    alt={`s17-${i}`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                </div>

                {/* Text container */}
                <div
                  style={{
                    position: "absolute",
                    top: `${textTop}px`,
                    left: `${left}px`,
                    width: `${colWidth}px`,
                    height: "80px",
                    background: "#b9b9b9ff",
                    borderBottomLeftRadius: "8px",
                    borderBottomRightRadius: "8px",
                    padding: "8px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    textAlign: "center",
                  }}
                >
                  <div style={{ fontSize: "12px", color: "#000", lineHeight: "1.2" }}>
                    Line 1 - Image {i + 1}
                  </div>
                  <div style={{ fontSize: "12px", color: "#000", lineHeight: "1.2" }}>
                    Line 2 - Image {i + 1}
                  </div>
                </div>

                {/* Hidden file input */}
                <input
                  type="file"
                  accept="image/*"
                  ref={(el) => (hiddenFileInputs.current[i] = el)}
                  style={{ display: "none" }}
                  onChange={(e) =>
                    onProductImageSelect && onProductImageSelect(i, e.target.files[0])
                  }
                />
              </React.Fragment>
            );
          })}
        </>
      )}

      {styleType === "style_18" && (
        <>
          {[0, 1, 2, 3, 4, 5].map((i) => {
            const isLeft = i < 3; // 0,1,2 → left column | 3,4,5 → right column
            const colWidth = 120; // circle diameter
            const colHeight = 120; // circle diameter
            const gapY = 40; // space between circles
            const top = 50 + (i % 3) * (colHeight + 60); // 60 = image + text spacing
            const left = isLeft ? 60 : 200; // adjust based on parent ~380px wide

            return (
              <React.Fragment key={`s18-${i}`}>
                {/* Circular Image */}
                <div
                  style={{
                    position: "absolute",
                    top: `${top}px`,
                    left: `${left}px`,
                    width: `${colWidth}px`,
                    height: `${colHeight}px`,
                    borderRadius: "50%",
                    overflow: "hidden",
                    boxShadow: "0 6px 18px rgba(0,0,0,0.25)",
                    cursor: "pointer",
                    background: "#fff",
                  }}
                  onClick={() => handleProductClick(i)}
                >
                  <img
                    src={
                      productImages[i] ||
                      `https://placehold.co/${colWidth}x${colHeight}`
                    }
                    alt={`s18-${i}`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                </div>

                {/* Text container */}
                <div
                  style={{
                    position: "absolute",
                    top: `${top + colHeight + 8}px`, // margin below circle
                    left: `${left}px`,
                    width: `${colWidth}px`,
                    height: "50px",
                    background: "#b9b9b9ff",
                    borderRadius: "8px",
                    padding: "8px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    textAlign: "center",
                  }}
                >
                  <div style={{ fontSize: "12px", color: "#000", lineHeight: "1.2" }}>
                    Line 1 - Image {i + 1}
                  </div>
                  <div style={{ fontSize: "12px", color: "#000", lineHeight: "1.2" }}>
                    Line 2 - Image {i + 1}
                  </div>
                </div>

                {/* Hidden file input */}
                <input
                  type="file"
                  accept="image/*"
                  ref={(el) => (hiddenFileInputs.current[i] = el)}
                  style={{ display: "none" }}
                  onChange={(e) =>
                    onProductImageSelect && onProductImageSelect(i, e.target.files[0])
                  }
                />
              </React.Fragment>
            );
          })}
        </>
      )}

    {styleType === "style_19" && (
      <>
        {[0, 1, 2, 3, 4, 5].map((i) => {
          const colWidth = (380 - 30) / 2; // parent 380px, 2 columns
          const startTop = 40;
          const gap = 20;

          // Adjusted heights so both cols = 667
          const tallHeight = 120;
          const shortHeight = 90;
          const rightTallHeight = 280; // balances with left stack

          let top = startTop;
          let left = "20px";
          let width = colWidth;
          let height = shortHeight;

          if (i === 0) {
            // Left col, first (tall)
            top = startTop;
            left = "20px";
            height = tallHeight;
          } else if (i === 1) {
            // Left col, second (tall)
            top = startTop + tallHeight + gap + 20;
            left = "20px";
            height = tallHeight;
          } else if (i === 2) {
            // Left col, third (short)
            top = startTop + tallHeight * 2 + gap * 2 + 19 * 2;
            left = "20px";
            height = shortHeight;
          } else if (i === 3) {
            // Right col, first (short)
            top = startTop;
            left = `calc(100% - ${colWidth + 20}px)`;
            height = shortHeight;
          } else if (i === 4) {
            // Right col, second (tall)
            top = startTop + shortHeight + gap + 20;
            left = `calc(100% - ${colWidth + 20}px)`;
            height = rightTallHeight;
          } else if (i === 5) {
            // Full-width bottom row
            top = startTop + tallHeight * 2 + shortHeight + gap * 6;
            left = "20px";
            width = colWidth * 2;
            height = shortHeight;
          }

          const textTop = top + height;

          return (
            <React.Fragment key={`s19-${i}`}>
              {/* Image */}
              <div
                style={{
                  position: "absolute",
                  top: `${top}px`,
                  left,
                  width: `${width}px`,
                  height: `${height}px`,
                  borderRadius: "8px",
                  overflow: "hidden",
                  boxShadow: "0 6px 18px rgba(0,0,0,0.25)",
                  cursor: "pointer",
                  background: "#fff",
                }}
                onClick={() => handleProductClick(i)}
              >
                <img
                  src={
                    productImages[i] ||
                    `https://placehold.co/${Math.round(width)}x${Math.round(height)}`
                  }
                  alt={`s19-${i}`}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                  }}
                />
              </div>

              {/* Text box */}
              <div
                style={{
                  position: "absolute",
                  top: `${textTop}px`,
                  left,
                  width: `${width}px`,
                  height: "35px",
                  background: "#b9b9b9ff",
                  borderBottomLeftRadius: "8px",
                  borderBottomRightRadius: "8px",
                  padding: "8px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  textAlign: "center",
                }}
              >
                <div style={{ fontSize: "12px", color: "#000", lineHeight: "1.2" }}>
                  Line 1 - Image {i + 1}
                </div>
                <div style={{ fontSize: "12px", color: "#000", lineHeight: "1.2" }}>
                  Line 2 - Image {i + 1}
                </div>
              </div>

              {/* Hidden input */}
              <input
                type="file"
                accept="image/*"
                ref={(el) => (hiddenFileInputs.current[i] = el)}
                style={{ display: "none" }}
                onChange={(e) =>
                  onProductImageSelect && onProductImageSelect(i, e.target.files[0])
                }
              />
            </React.Fragment>
          );
        })}
      </>
    )}

    {styleType === "style_20" && (
  <>
    {[0, 1, 2, 3, 4, 5].map((i) => {
      const parentWidth = 380;
      const gap = 10;
      const colWidth = (parentWidth - gap * 3) / 2;
      const startTop = 40;

      let top = startTop;
      let left = gap;
      let width = colWidth;
      let height = 160;

      // Layout mapping
      if (i === 0) {
        // Left col, top tall
        top = startTop;
        left = gap;
        height = 150;
      } else if (i === 1) {
        // Left col, middle short
        top = startTop + 180 + gap + 15;
        left = gap;
        height = 100;
      } else if (i === 2) {
        // Left col, bottom tall
        top = startTop + 150 + 160 + gap * 2 + 30;
        left = gap;
        height = 170;
      } else if (i === 3) {
        // Right col, top short
        top = startTop;
        left = colWidth + gap * 2;
        height = 120;
      } else if (i === 4) {
        // Right col, middle short
        top = startTop + 120 + gap + 45;
        left = colWidth + gap * 2;
        height = 120;
      } else if (i === 5) {
        // Right col, bottom tall
        top = startTop + 120 + 110 + gap * 2 + 50 * 2;
        left = colWidth + gap * 2;
        height = 180;
      }

      const textTop = top + height;

      return (
        <React.Fragment key={`s20-${i}`}>
          {/* Image */}
          <div
            style={{
              position: "absolute",
              top: `${top}px`,
              left: `${left}px`,
              width: `${width}px`,
              height: `${height}px`,
              borderRadius: "8px",
              overflow: "hidden",
              boxShadow: "0 6px 18px rgba(0,0,0,0.25)",
              cursor: "pointer",
              background: "#fff",
            }}
            onClick={() => handleProductClick(i)}
          >
            <img
              src={
                productImages[i] ||
                `https://placehold.co/${Math.round(width)}x${Math.round(height)}`
              }
              alt={`s20-${i}`}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
          </div>

          {/* Text box */}
          <div
            style={{
              position: "absolute",
              top: `${textTop}px`,
              left,
              width: `${width}px`,
              height: "50px",
              background: "#ffffff45",
              borderBottomLeftRadius: "8px",
              borderBottomRightRadius: "8px",
              padding: "8px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: "12px", color: "#fff", lineHeight: "1.2" , fontWeight: 'bold'}}>
              Line 1 - Image {i + 1}
            </div>
            <div style={{ fontSize: "12px", color: "#fff", lineHeight: "1.2" , fontWeight: 'bold'}}>
              Line 2 - Image {i + 1}
            </div>
          </div>

          {/* Hidden input */}
          <input
            type="file"
            accept="image/*"
            ref={(el) => (hiddenFileInputs.current[i] = el)}
            style={{ display: "none" }}
            onChange={(e) =>
              onProductImageSelect && onProductImageSelect(i, e.target.files[0])
            }
          />
        </React.Fragment>
      );
    })}
  </>
)}

    </div>
  );
};